/**
 * Onboarding — 6-step wizard for new web signups (matching the mobile app).
 * Route: /onboarding
 *
 * Steps:
 *   1. Enter your first name
 *   2. Choose relationship focus
 *   3. Choose communication style
 *   4. Biggest relationship challenge
 *   5. Preferred communication method
 *   6. What support would help most
 *
 * Data writes to both `users/{uid}` and `users/{uid}/preferences/userProfile`
 * (matching the mobile pattern). On completion, marks onboarding done in
 * `users/{uid}/meta/firstLoad` and fires conversation starters.
 *
 * Shown automatically after signup if onboarding not completed.
 */

import React, { useEffect, useState } from 'react';
import {
  Loader,
  User,
  Users,
  MessageCircle,
  AlertTriangle,
  Phone,
  Shield,
} from 'react-feather';
import { getFeatherIcon } from '../utils/iconMap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  saveOnboardingStep,
  completeOnboarding,
  getOnboardingStatus,
  skipOnboarding,
} from '../services/accountService';
import { capture } from '../services/posthog';
import {
  RELATIONSHIP_FOCUS_OPTIONS,
  COMMUNICATION_STYLE_OPTIONS,
  RELATIONSHIP_CHALLENGE_OPTIONS,
  COMMUNICATION_METHOD_OPTIONS,
  SUPPORT_NEEDED_OPTIONS,
} from '../types/account';
import type {
  RelationshipFocus,
  CommunicationStyle,
  RelationshipChallenge,
  CommunicationMethod,
  SupportNeeded,
} from '../types/account';

const TOTAL_STEPS = 6;

const STEP_ICONS = [User, Users, MessageCircle, AlertTriangle, Phone, Shield];

// Stable, backend-neutral labels for the onboarding funnel (step index → name).
const STEP_NAMES = [
  'name',
  'relationship_focus',
  'communication_style',
  'relationship_challenge',
  'communication_method',
  'support_needed',
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid;

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState(user?.firstName || user?.displayName || '');
  const [relationshipFocus, setRelationshipFocus] = useState<RelationshipFocus | ''>('');
  const [communicationStyle, setCommunicationStyle] = useState<CommunicationStyle | ''>('');
  const [relationshipChallenge, setRelationshipChallenge] = useState<RelationshipChallenge | ''>('');
  const [communicationMethod, setCommunicationMethod] = useState<CommunicationMethod | ''>('');
  const [supportNeeded, setSupportNeeded] = useState<SupportNeeded | ''>('');
  const [saving, setSaving] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // ─── Check if already onboarded ─────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    (async () => {
      setCheckingStatus(true);
      try {
        const done = await getOnboardingStatus(uid);
        if (done) {
          navigate('/', { replace: true });
          return;
        }
      } catch {
        // Continue to onboarding
      }
      // Fired only once we're committed to showing onboarding — a redirected-away
      // (already-onboarded) visit returns above and never counts.
      capture('onboarding_started', {
        platform: 'web',
        entry_path: window.location.pathname,
      });
      setCheckingStatus(false);
    })();
  }, [uid, navigate]);

  // ─── Navigation ─────────────────────────────────────────────────────
  const handleNext = async () => {
    if (!uid) return;
    setSaving(true);

    const stepName = STEP_NAMES[step - 1];
    // The chosen enum value for this step. Step 1 is the free-text name (PII) —
    // never send its value; report null so the funnel still sees the step.
    const selection =
      step === 2 ? relationshipFocus :
      step === 3 ? communicationStyle :
      step === 4 ? relationshipChallenge :
      step === 5 ? communicationMethod :
      step === 6 ? supportNeeded :
      null;

    let saveSucceeded = true;
    let completed = false;
    try {
      if (step === 1 && firstName.trim()) {
        await saveOnboardingStep(uid, { firstName: firstName.trim(), userName: firstName.trim() });
      } else if (step === 2 && relationshipFocus) {
        await saveOnboardingStep(uid, { relationshipFocus });
      } else if (step === 3 && communicationStyle) {
        await saveOnboardingStep(uid, { communicationStyle });
      } else if (step === 4 && relationshipChallenge) {
        await saveOnboardingStep(uid, { relationshipChallenge });
      } else if (step === 5 && communicationMethod) {
        await saveOnboardingStep(uid, { communicationMethod });
      } else if (step === 6 && supportNeeded) {
        await saveOnboardingStep(uid, { supportNeeded });
        await completeOnboarding(uid);
        completed = true;
      }
    } catch {
      // Silently continue — data can be filled in later via Edit Profile
      saveSucceeded = false;
    }

    capture('onboarding_step_completed', {
      step,
      step_name: stepName,
      selection,
      save_succeeded: saveSucceeded,
      platform: 'web',
    });

    if (completed) {
      // Client mirror of the server `onboarding_finished` (funnels take first
      // occurrence); belt-and-braces against the fire-and-forget callable.
      capture('onboarding_finished', {
        outcome: 'completed',
        device: 'web',
        has_preferences: true,
      });
      setSaving(false);
      navigate('/', { replace: true });
      return;
    }

    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    }
    setSaving(false);
  };

  const handleSkip = async () => {
    if (!uid) return;
    const hasPreferences = !!(
      relationshipFocus ||
      communicationStyle ||
      relationshipChallenge ||
      communicationMethod ||
      supportNeeded
    );
    // Emitted up-front so the skip is recorded regardless of the server write.
    // step_skipped_at = the step number being viewed (1-6) to match the app's
    // convention (only the app's pre-step start screen ever sends 0).
    capture('onboarding_skipped', { step_skipped_at: step, platform: 'web' });
    capture('onboarding_finished', {
      outcome: 'skipped',
      device: 'web',
      has_preferences: hasPreferences,
    });
    setSaving(true);
    try {
      await skipOnboarding(uid);
    } catch {
      // fine
    } finally {
      setSaving(false);
      navigate('/', { replace: true });
    }
  };

  const canProceed =
    (step === 1 && firstName.trim().length >= 1) ||
    (step === 2 && relationshipFocus !== '') ||
    (step === 3 && communicationStyle !== '') ||
    (step === 4 && relationshipChallenge !== '') ||
    (step === 5 && communicationMethod !== '') ||
    (step === 6 && supportNeeded !== '');

  // ─── Render ─────────────────────────────────────────────────────────

  if (!user || checkingStatus) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-loading">
          <Loader size={24} className="spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        {/* Icon-based progress indicator */}
        <div className="onboarding-step-indicator">
          {STEP_ICONS.map((Icon, i) => {
            const stepNum = i + 1;
            const isActive = stepNum <= step;
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div
                    className={`onboarding-progress-line ${stepNum <= step ? 'active' : ''}`}
                  />
                )}
                <div className={`onboarding-step-icon ${isActive ? 'active' : ''}`}>
                  <Icon size={18} />
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Name */}
        {step === 1 && (
          <div className="onboarding-step">
            <h2>what's your name?</h2>
            <p className="onboarding-subtitle">let us know what to call you</p>
            <input
              type="text"
              className="onboarding-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value.slice(0, 30).replace(/[^a-zA-Z\s'-]/g, ''))}
              placeholder="enter your name"
              maxLength={30}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && canProceed && handleNext()}
            />
          </div>
        )}

        {/* Step 2: Relationship Focus */}
        {step === 2 && (
          <div className="onboarding-step">
            <h2>what's your primary relationship focus right now?</h2>
            <div className="onboarding-options">
              {RELATIONSHIP_FOCUS_OPTIONS.map((opt) => {
                const Icon = getFeatherIcon(opt.icon);
                return (
                  <button
                    key={opt.value}
                    className={`onboarding-option ${relationshipFocus === opt.value ? 'selected' : ''}`}
                    onClick={() => setRelationshipFocus(opt.value)}
                  >
                    <Icon size={16} />
                    <span>{opt.label.toLowerCase()}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Communication Style */}
        {step === 3 && (
          <div className="onboarding-step">
            <h2>how do you prefer to communicate in terms of style?</h2>
            <div className="onboarding-options">
              {COMMUNICATION_STYLE_OPTIONS.map((opt) => {
                const Icon = getFeatherIcon(opt.icon);
                return (
                  <button
                    key={opt.value}
                    className={`onboarding-option ${communicationStyle === opt.value ? 'selected' : ''}`}
                    onClick={() => setCommunicationStyle(opt.value)}
                  >
                    <Icon size={16} />
                    <span>{opt.label.toLowerCase()}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Relationship Challenge */}
        {step === 4 && (
          <div className="onboarding-step">
            <h2>what's your biggest relationship challenge?</h2>
            <div className="onboarding-options">
              {RELATIONSHIP_CHALLENGE_OPTIONS.map((opt) => {
                const Icon = getFeatherIcon(opt.icon);
                return (
                  <button
                    key={opt.value}
                    className={`onboarding-option ${relationshipChallenge === opt.value ? 'selected' : ''}`}
                    onClick={() => setRelationshipChallenge(opt.value)}
                  >
                    <Icon size={16} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 5: Communication Method */}
        {step === 5 && (
          <div className="onboarding-step">
            <h2>which communication methods do you use most often?</h2>
            <div className="onboarding-options">
              {COMMUNICATION_METHOD_OPTIONS.map((opt) => {
                const Icon = getFeatherIcon(opt.icon);
                return (
                  <button
                    key={opt.value}
                    className={`onboarding-option ${communicationMethod === opt.value ? 'selected' : ''}`}
                    onClick={() => setCommunicationMethod(opt.value)}
                  >
                    <Icon size={16} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 6: Support Needed */}
        {step === 6 && (
          <div className="onboarding-step">
            <h2>what support would help you most right now?</h2>
            <div className="onboarding-options">
              {SUPPORT_NEEDED_OPTIONS.map((opt) => {
                const Icon = getFeatherIcon(opt.icon);
                return (
                  <button
                    key={opt.value}
                    className={`onboarding-option ${supportNeeded === opt.value ? 'selected' : ''}`}
                    onClick={() => setSupportNeeded(opt.value)}
                  >
                    <Icon size={16} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="onboarding-actions">
          <button className="btn btn-ghost btn-sm" onClick={handleSkip} disabled={saving}>
            skip onboarding
          </button>
          <button
            className="btn btn-primary"
            onClick={handleNext}
            disabled={!canProceed || saving}
          >
            {saving ? 'saving...' : step === TOTAL_STEPS ? 'get started' : 'next'}
          </button>
        </div>
      </div>
    </div>
  );
}
